import { ScreenReaderInfo } from "../../components/common/ScreenReaderInfo"
import { AlertContainerComponent } from "../../ui/containers/AlertContainer"
import { Box, Button, styled, useMediaQuery, useTheme } from "@mui/material"
import {
  FC,
  KeyboardEvent,
  MutableRefObject,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import {
  addStyles,
  EditableMathField,
  StaticMathField,
  MathField,
  MathFieldConfig
} from "react18-mathquill"
import { EquationEditorControls, EquationEditorSection } from "./components"
import { useControlsKeyboardNav, useEquationEditorAnnouncement } from "./hooks"
import { SectionType } from "./types"
import {
  checkInsertionOfSup,
  findNotIncludingSymbols,
  getBackSlashActions,
  getMathFieldControls,
  getOperatorsStructure,
  getUniqOperators,
  scrollIntoView
} from "./utils"
import Loader from "../../ui/common/Loader"
import { focusStyle } from "../../theme"
import { renderKatexAccessible } from "../../mathML/renderKatexAccessible"
import { Variant } from "../../types"
import { REVIEW_BADGE_ORIGIN, ReviewBadge } from "../../ui/badges/ReviewBadge"

export type EquationEditorProps = {
  children?: JSX.Element | JSX.Element[]
  disabled?: boolean
  value: string
  onChange: (value: string) => void
  onUndo: (steps?: number) => void
  onRedo: (steps?: number) => void
  onSubmit: () => void
  showSubmit: boolean
  variant: Variant
  badge: boolean
  shouldShowCorrectAnswer: boolean
}

addStyles()

const mathFieldConfig: MathFieldConfig = {
  maxDepth: 10
}

const UNIDENTIFIED_KEY = "Unidentified"

export const EquationEditor: FC<EquationEditorProps> = ({
  children,
  disabled,
  value,
  onChange,
  onUndo,
  onRedo,
  onSubmit,
  showSubmit = false,
  variant,
  badge,
  shouldShowCorrectAnswer
}) => {
  const [numbersSections, actionsSections, prompt] = useMemo(
    () => getOperatorsStructure(children),
    [children]
  )
  const uniqOperators = useMemo(
    () => getUniqOperators(numbersSections, actionsSections),
    [numbersSections, actionsSections]
  )
  const backSlashActions = useMemo(
    () => getBackSlashActions(uniqOperators),
    [uniqOperators]
  )

  //ref for math-quill
  const mathFieldRef = useRef<MathField>()
  //ref for mathlive with method getValue for decoding latex to spoken text
  const controlsRef = useRef<HTMLButtonElement[]>([])
  const eqEditorWrapperRef = useRef<HTMLElement>()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"))

  const {
    currentControl,
    onKeyDown: controlsKeyDownHandler,
    selectControl
  } = useControlsKeyboardNav(
    controlsRef as MutableRefObject<HTMLButtonElement[]>
  )

  const [inputSymbols, setInputSymbols] = useState<string>("")
  const [noneKeyFlag, setNoneKeyFlag] = useState(false)

  const announcement = useEquationEditorAnnouncement(
    renderKatexAccessible(value).speakable
  )

  const insertCommand = useCallback(
    (value: string) => () => {
      if (disabled) return
      try {
        if (checkInsertionOfSup(value, mathFieldRef)) return

        mathFieldRef.current.cmd(value)
        //scrollIntoView(mathFieldRef.current)
      } catch (err) {
        console.log(err)
      }
    },
    [disabled] // Prevent user adding more num/sym once answer is finished
  )

  const isErrorInput = useMemo(
    () =>
      noneKeyFlag &&
      findNotIncludingSymbols(value, uniqOperators, backSlashActions),
    [noneKeyFlag, value, uniqOperators, backSlashActions]
  )

  const mathFieldControls = useMemo(
    () => getMathFieldControls(mathFieldRef.current, onUndo, onRedo),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mathFieldRef.current, onUndo, onRedo]
  )

  const mathFieldKeyDownHandler = (e: KeyboardEvent<HTMLSpanElement>) => {
    e.preventDefault()

    const key = e.key

    if (key === UNIDENTIFIED_KEY) {
      setNoneKeyFlag(true)
      return
    }

    if (checkInsertionOfSup(key, mathFieldRef)) return

    if (key === "Tab") {
      e.shiftKey
        ? eqEditorWrapperRef.current?.focus()
        : controlsRef.current[currentControl]?.focus()
      return
    }

    if (key === " " || uniqOperators.some(op => op.includes(key))) {
      setInputSymbols(state => `${state}${key}`)
    }
    if (key === " " || uniqOperators.includes(key)) {
      mathFieldRef.current?.cmd(key)
    }

    scrollIntoView(mathFieldRef.current)
    noneKeyFlag && setNoneKeyFlag(false)
  }

  useEffect(() => {
    const findCoincidence = backSlashActions.find(action =>
      inputSymbols.includes(action)
    )
    if (findCoincidence) {
      mathFieldRef.current?.cmd(findCoincidence)
      setInputSymbols("")
    }
  }, [inputSymbols, backSlashActions])

  useEffect(() => {
    const textarea = (mathFieldRef as any).current?.__controller.textareaSpan[0]
      .firstChild

    if (!textarea) return

    textarea.ariaLabel = renderKatexAccessible(value).speakable || "Answer Field"
  }, [value])

  useEffect(() => {
    const deleteAction = (e: any) => {
      if (e.code !== "Backspace") return

      mathFieldRef.current?.keystroke("Backspace")
    }

    document.addEventListener("keydown", deleteAction)

    return () => document.removeEventListener("keydown", deleteAction)
  }, [])

  return (
    <Suspense fallback={<Loader />}>
      {prompt}
      <ScreenReaderInfo ariaLive="polite" ariaAtomic ariaBusy={!announcement}>
        {announcement}
      </ScreenReaderInfo>

      <EquationEditorInteractionWrapper
        ref={eqEditorWrapperRef}
        tabIndex={0}
        aria-label="Equation Editor"
      >
        {isErrorInput && (
          <AlertContainerComponent
            title="Input Error"
            severity="error"
          >{`Input has incorrect symbols or actions!!!`}</AlertContainerComponent>
        )}

        <MathFieldWithBadge>
          <ReviewBadge
            correct={badge}
            anchorOrigin={REVIEW_BADGE_ORIGIN.topLeft}
            badgeBorder={true}
            style={{
              left: 0,
              top: 10
            }}
          />
          {shouldShowCorrectAnswer ? (
            <StyledStaticMathField disabled={disabled} variant={variant}>
              {value}
            </StyledStaticMathField>
          ) : (
            <StyledEditableMathField
              latex={value}
              onChange={mathField => onChange(mathField?.latex())}
              mathquillDidMount={mathField =>
                (mathFieldRef.current = mathField)
              }
              config={mathFieldConfig}
              onKeyDown={mathFieldKeyDownHandler}
              disabled={disabled}
              variant={variant}
            />
          )}
        </MathFieldWithBadge>
        <EquationEditorWrapper>
          <EquationEditorHeader>
            <EquationEditorControls
              ref={controlsRef}
              currentControl={currentControl}
              onKeyDown={controlsKeyDownHandler}
              selectControl={selectControl}
              mathFieldControls={mathFieldControls}
              disabled={disabled}
            />
          </EquationEditorHeader>
          <EquationEditorBody>
            {numbersSections.map((section, i) => (
              <EquationEditorSection
                isMobile={isMobile}
                key={`Numbers${i}`}
                sectionType={SectionType.numbersSection}
                sectionOperators={section}
                insertCommand={insertCommand}
              />
            ))}
            {actionsSections.map((section, i) => (
              <EquationEditorSection
                isMobile={isMobile}
                key={`Actions${i}`}
                sectionType={SectionType.actionsSections}
                sectionOperators={section}
                insertCommand={insertCommand}
              />
            ))}
          </EquationEditorBody>
        </EquationEditorWrapper>
        {!!showSubmit && (
          <Box display="flex" justifyContent="flex-end" mt={4}>
            <Button
              variant="outlined"
              size="medium"
              color="grey"
              onClick={() => onSubmit()}
              disabled={disabled || !value?.trim()}
            >
              Submit
            </Button>
          </Box>
        )}
      </EquationEditorInteractionWrapper>
    </Suspense>
  )
}

interface StyledEditableMathFieldProps {
  disabled: boolean
  variant: Variant
}

interface StyledStaticMathFieldProps {
  disabled: boolean
  variant: Variant
}

const getVariantStyles = (theme, variant) => {
  switch (variant) {
    case Variant.correct:
      return {
        backgroundColor: theme.palette.lightGreen[100],
        borderColor: theme.palette.lightGreen[300]
      }
    case Variant.invalid:
      return {
        backgroundColor: theme.palette.orange[100],
        borderColor: theme.palette.orange[300]
      }
    case Variant.neutral:
      return {
        backgroundColor: theme.palette.blue["A900"],
        borderColor: theme.palette.blue["A800"]
      }
    default:
      return {
        backgroundColor: "transparent",
        borderColor: theme.palette.grey[200]
      }
  }
}

const EquationEditorInteractionWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("mobile")]: {
    maxWidth: "325px"
  },
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5)
}))

const MathFieldWithBadge = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  marginBottom: theme.spacing(1)
}))

const StyledEditableMathField = styled(
  EditableMathField
)<StyledEditableMathFieldProps>(({ theme, disabled, variant }) => {
  const variantStyles = getVariantStyles(theme, variant)

  return {
    display: "flex !important",
    alignItems: "center",
    width: "100%",
    minHeight: "37px",
    border: `2px solid ${variantStyles.borderColor} !important`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.grey[600],
    boxShadow: "none !important",
    backgroundColor: `${variantStyles.backgroundColor} !important`,
    paddingLeft: theme.spacing(2),

    // Hide math quill default textarea insertion
    "& .mq-empty": {
      backgroundColor: "transparent !important"
    },

    //! Does not seem like these were needed but leaving here for future potentially.
    // This CSS acts as a safety net to ensure the textarea is hidden even if the mq-editable-field class is lost during re-renders.
    // "& .mq-textarea": {
    //   position: "relative",
    //   userSelect: "text"
    // },

    // "& .mq-textarea *": {
    //   userSelect: "text",
    //   position: "absolute",
    //   clip: "rect(1em, 1em, 1em, 1em)"
    // },

    ":focus-within": {
      ...(!disabled
        ? {
            // borderColor: `${focusStyle(theme).colors.border} !important`,
            outline: focusStyle(theme).border.outline,
            border: `1px solid ${focusStyle(theme).colors.border} !important`
          }
        : {})
    }
  }
})

const StyledStaticMathField = styled(
  StaticMathField
)<StyledStaticMathFieldProps>(({ theme, variant }) => {
  const variantStyles = getVariantStyles(theme, variant)
  return {
    display: "flex !important",
    alignItems: "center",
    width: "100%",
    minHeight: "37px",
    border: `2px solid ${variantStyles.borderColor} !important`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.grey[600],
    boxShadow: "none !important",
    backgroundColor: `${variantStyles.backgroundColor} !important`,
    paddingLeft: theme.spacing(2)
  }
})

const EquationEditorWrapper = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: `${theme.shape.borderRadius * 1.5}px`
}))

const EquationEditorHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette.grey[200]}`
}))

const EquationEditorBody = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(0.5),
  width: "100%",
  overflow: "auto",

  [theme.breakpoints.down("mobile")]: {
    display: "block"
  }
}))
